import { useState, useEffect } from "react";
import CookieConsent from "react-cookie-consent";
import { useCookies } from "react-cookie";
import {
    Box,
    Button,
    Text,
    VStack,
    Collapse,  // To show/hide cookie information
} from "@chakra-ui/react";
import { MdOutlineQuestionMark } from "react-icons/md";
import { v4 as uuidv4 } from "uuid";

// 🍪 Create encoded enriched cookie
const createConsentCookie = (category, accepted) => {
    const cookieValue = {
        accepted,
        category,
        consentId: uuidv4(),
        timestamp: new Date().toISOString()
    };
    return encodeURIComponent(JSON.stringify(cookieValue));
};

const CookieConsentBanner = () => {
    const [cookies, setCookie, removeCookie] = useCookies([]);
    const [consentGiven, setConsentGiven] = useState(false);

    // States to manage visibility of cookie info
    const [showItalianInfo, setShowItalianInfo] = useState(false);
    const [showEnglishInfo, setShowEnglishInfo] = useState(false);

    // Check for the existence of TechnicalGrins cookie and remove it if it exists
    useEffect(() => {
        if (cookies.TechnicalGrins) {
            // Remove TechnicalGrins cookie if it exists
            removeCookie("TechnicalGrins", { path: "/", secure: true, sameSite: "Strict" });
        }

        // Always set essential cookie on mount
        setCookie("TechnicalCookieGrins", createConsentCookie("TechnicalCookieGrins", true), {
            path: "/",
            secure: true,
            sameSite: "Strict"
        });
    }, [cookies, removeCookie, setCookie]);

    const handleAcceptAll = () => {
        // setCookie("TechnicalGrins", createConsentCookie("technical", true), {
        //     path: "/",
        //     secure: true,
        //     sameSite: "Strict"
        // });
        document.cookie = "CookieConsent=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
        setConsentGiven(true);
    };

    // Toggle functions to show cookie info for each language
    const toggleItalianInfo = () => {
        // When toggling Italian info, close English info if open
        setShowItalianInfo(!showItalianInfo);
        if (showEnglishInfo) setShowEnglishInfo(false);
    };

    const toggleEnglishInfo = () => {
        // When toggling English info, close Italian info if open
        setShowEnglishInfo(!showEnglishInfo);
        if (showItalianInfo) setShowItalianInfo(false);
    };

    return (
        <>
            {/* 🚫 BLOCKING OVERLAY UNTIL CONSENT */}
            {!consentGiven && (
                <Box
                    position="fixed"
                    top="0"
                    left="0"
                    w="100vw"
                    h="100vh"
                    bg="rgba(0, 0, 0, 0.4)"
                    zIndex="9998"
                    backdropFilter="blur(2px)"
                    pointerEvents="all"
                />
            )}

            <Box zIndex="9999" position="relative">
                {!consentGiven && (
                    <CookieConsent
                        location="bottom"
                        buttonText="Close"
                        onAccept={handleAcceptAll}
                        style={{
                            background: "#9acd32",
                            textAlign: "center",
                            padding: "20px 15px",
                            zIndex: 9999
                        }}
                        buttonStyle={{
                            background: "#0b4e5a",
                            color: "white",
                            fontSize: "13px",
                            borderRadius: "8px",
                            padding: "6px 16px"
                        }}
                        cookieName={undefined}
                        cookieExpires={null}
                    >
                        <Box maxW="5xl" mx="auto" color="white" fontSize="14px" textAlign="justify">
                            <VStack spacing={3} align="stretch">
                                {/* 🇮🇹 Italian */}
                                <Text>
                                    Attualmente questo sito utilizza esclusivamente{" "}
                                    <Text
                                        as="span"
                                        fontWeight="semibold"
                                        textDecoration="underline"
                                        cursor="pointer"
                                        onClick={toggleItalianInfo}  // 👈 Clickable text for Italian
                                    >
                                        Cookie Tecnici
                                    </Text>{" "}
                                    necessari per garantire la corretta navigazione e non utilizza cookies di profilazione o analitici.
                                    Attualmente i cookies hanno una scadenza legata alla sessione utente di navigazione.
                                </Text>

                                {/* Display additional information when clicked */}
                                <Collapse in={showItalianInfo}>
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        bg="#0b4e5a"
                                        p="2"
                                        borderRadius="8px"
                                    >
                                        <MdOutlineQuestionMark color="white" style={{ marginRight: "8px" }} size={20} />
                                        <Text fontSize="sm" color="white">
                                            I cookie sono piccoli file di testo che il sito invia al terminale dell'utente, ove vengono memorizzati per poi essere ritrasmessi al sito alla visita successiva.
                                        </Text>
                                    </Box>
                                </Collapse>

                                {/* 🇬🇧 English */}
                                <Text>
                                    At the time, this website uses{" "}
                                    <Text
                                        as="span"
                                        fontWeight="semibold"
                                        textDecoration="underline"
                                        cursor="pointer"
                                        onClick={toggleEnglishInfo}  // 👈 Clickable text for English
                                    >
                                        Technical Cookies
                                    </Text>{" "}
                                    only, necessary to guarantee the best navigation experience. This website doesn't use profiling or analytics cookies.
                                    Currently, our cookies have a session-related expiration.
                                </Text>

                                {/* Display additional information when clicked */}
                                <Collapse in={showEnglishInfo}>
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        bg="#0b4e5a"
                                        p="2"
                                        borderRadius="8px"
                                    >
                                        <MdOutlineQuestionMark color="white" style={{ marginRight: "8px" }} size={20} />
                                        <Text fontSize="sm" color="white">
                                            Cookies are small text files that the website sends via the User's terminal, wherever they're memorized to be, then send to the website for the User's further accesses.
                                        </Text>
                                    </Box>
                                </Collapse>
                            </VStack>
                        </Box>
                    </CookieConsent>
                )}
            </Box>
        </>
    );
};

export default CookieConsentBanner;
